import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
// Subpath import, NOT the '@edge/charts' barrel: this provider mounts eagerly
// in main.tsx, and a value import of the barrel would pull klinecharts + all
// of charts-core (via EdgeChart's module-level register side effects) into
// the entry bundle. The subpath resolves to the side-effect-free
// types/interaction.ts module.
import { DEFAULT_INTERACTION_SETTINGS, INTERACTION_PRESETS } from '@edge/charts/interaction'
import type { ChartInteractionSettings } from '@edge/charts/interaction'
import { ChartInteractionContext } from './chart-interaction-hooks'

const STORAGE_KEY = 'edge_chart_interaction'

function loadStored(): ChartInteractionSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_INTERACTION_SETTINGS }
    const parsed = JSON.parse(raw) as Partial<ChartInteractionSettings>
    return { ...DEFAULT_INTERACTION_SETTINGS, ...parsed }
  } catch {
    return { ...DEFAULT_INTERACTION_SETTINGS }
  }
}

function save(next: ChartInteractionSettings): ChartInteractionSettings {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  return next
}

/**
 * Per-user, global interaction feel — mouse wheel / drag grammar shared by
 * every chart on screen. Mirrors the theme-editor's provider pattern
 * (`settings-context.tsx`), but deliberately separate: interaction settings
 * are not merged into `ChartSettings`, so no per-panel record can ever hold
 * a stale copy that diverges from the global choice (design D1/D4).
 */
export function ChartInteractionProvider({ children }: { children: ReactNode }) {
  const [interaction, setInteraction] = useState<ChartInteractionSettings>(loadStored)

  const update = useCallback((patch: Partial<ChartInteractionSettings>) => {
    setInteraction((prev) => save({ ...prev, ...patch }))
  }, [])

  const applyPreset = useCallback((id: string) => {
    const preset = INTERACTION_PRESETS.find((p) => p.id === id)
    if (!preset) return
    // Merge, don't replace: presets set only the mouse/drag grammar, so a
    // non-grammar field like `historyVisibleWindow` must survive a preset switch.
    setInteraction((prev) => save({ ...prev, ...preset.settings }))
  }, [])

  return (
    <ChartInteractionContext value={{ interaction, update, applyPreset }}>
      {children}
    </ChartInteractionContext>
  )
}
